// pcosScreeningData.js
// All screen definitions, questions, options and scoring for PCOS Risk Screening

// ── Sidebar step list ─────────────────────────────────────────────────────────
export const PCOS_STEPS = [
  { id: 'intro',      label: 'Introduction' },
  { id: 'consent',    label: 'Consent' },
  { id: 'concerns',   label: 'Main Concerns' },
  { id: 'menstrual',  label: 'Menstrual Pattern' },
  { id: 'hormonal',   label: 'Hormonal Signs' },
  { id: 'metabolic',  label: 'Metabolic Indicators' },
  { id: 'fertility',  label: 'Fertility Context' },
  { id: 'emotional',  label: 'Emotional Wellness' },
  { id: 'review',     label: 'Review Summary' },
  { id: 'result',     label: 'Risk Result' },
];

// ── Flow order ────────────────────────────────────────────────────────────────
export const PCOS_FLOW = [
  'intro', 'consent', 'concerns',
  'menstrual', 'hormonal', 'metabolic', 'fertility', 'emotional',
  'review', 'result',
];

// ── Intro screen config ────────────────────────────────────────────────────────
export const PCOS_INTRO = {
  badge:    'PCOS Risk Check',
  badgeIcon: '🩺',
  heading:  'PCOS Risk Check',
  desc:     'A quick screening to understand your risk for Polycystic Ovary Syndrome (PCOS).',
  bullets: [
    { icon: '⏱️', text: 'Takes less than 7 minutes' },
    { icon: '🔒', text: 'Private & confidential' },
    { icon: '📋', text: 'Not a medical diagnosis' },
  ],
  privacy: 'Your health data is safe with us and will never be shared.',
  startLabel: 'Start screening',
  laterLabel: "I'll do it later",
  // SVG illustration key — rendered in PCOSRiskScreening.jsx
  illustrationKey: 'pcos',
};

// ── Consent screen config ─────────────────────────────────────────────────────
export const PCOS_CONSENT = {
  heading: 'Before we begin',
  paragraphs: [
    'This screening is for informational purposes only and not a medical diagnosis.',
    'Please confirm that you understand and consent to proceed.',
  ],
  checkLabel: 'I agree that this screening does not replace professional medical advice, diagnosis or treatment.',
  agreeLabel: 'I agree, continue',
};

// ── Main-concern chips ────────────────────────────────────────────────────────
export const PCOS_CONCERNS = [
  { id: 'irregularPeriods', label: 'Irregular periods',  icon: '🩸' },
  { id: 'acne',             label: 'Acne',               icon: '😔' },
  { id: 'excessHair',       label: 'Excess hair growth', icon: '🪒' },
  { id: 'weightGain',       label: 'Weight gain',        icon: '⚖️' },
  { id: 'hairThinning',     label: 'Hair thinning',      icon: '💆' },
  { id: 'fatigue',          label: 'Fatigue',            icon: '😴' },
  { id: 'conceiving',       label: 'Trouble conceiving', icon: '🤰' },
  { id: 'other',            label: 'Other',              icon: '···' },
];

// ── Question screens ──────────────────────────────────────────────────────────
export const PCOS_QUESTION_SCREENS = [
  {
    id: 'menstrual',
    stepLabel: 'Menstrual Pattern',
    title: 'Your Menstrual Pattern',
    subtitle: 'Understanding your cycle is the first step in PCOS screening.',
    icon: '🩸',
    questions: [
      {
        id: 'cycleRegularity',
        label: 'How would you describe your menstrual cycle?',
        type: 'toggle',
        options: [
          { label: 'Regular (21–35 days)',         score: 0 },
          { label: 'Irregular (varies greatly)',   score: 2 },
          { label: 'Absent (3+ months)',           score: 3 },
          { label: 'Infrequent (fewer than 8/yr)', score: 2 },
        ],
      },
      {
        id: 'cycleLength',
        label: 'What is your typical cycle length?',
        type: 'toggle',
        options: [
          { label: '21–35 days', score: 0 },
          { label: '36–45 days', score: 1 },
          { label: '46+ days',   score: 2 },
          { label: 'Not sure',   score: 1 },
        ],
      },
      {
        id: 'periodsPerYear',
        label: 'How many periods do you have per year?',
        type: 'toggle',
        options: [
          { label: '9 or more',   score: 0 },
          { label: '6–8',         score: 1 },
          { label: 'Fewer than 6',score: 2 },
          { label: 'None',        score: 3 },
        ],
      },
    ],
  },

  {
    id: 'hormonal',
    stepLabel: 'Hormonal Signs',
    title: 'Hormonal Signs',
    subtitle: 'These symptoms may indicate elevated androgen levels associated with PCOS.',
    icon: '⚗️',
    questions: [
      {
        id: 'acne',
        label: 'Do you experience acne or oily skin?',
        type: 'toggle',
        options: [
          { label: 'None',     score: 0 },
          { label: 'Mild',     score: 1 },
          { label: 'Moderate', score: 2 },
          { label: 'Severe',   score: 3 },
        ],
      },
      {
        id: 'hairGrowth',
        label: 'Do you have unwanted hair growth (face, chest, abdomen)?',
        type: 'toggle',
        options: [
          { label: 'No',          score: 0 },
          { label: 'Mild',        score: 1 },
          { label: 'Moderate',    score: 2 },
          { label: 'Significant', score: 3 },
        ],
      },
      {
        id: 'hairThinning',
        label: 'Do you experience hair thinning or scalp hair loss?',
        type: 'toggle',
        options: [
          { label: 'No',               score: 0 },
          { label: 'Mild thinning',    score: 1 },
          { label: 'Noticeable loss',  score: 2 },
          { label: 'Significant loss', score: 3 },
        ],
      },
    ],
  },

  {
    id: 'metabolic',
    stepLabel: 'Metabolic Indicators',
    title: 'Metabolic Indicators',
    subtitle: 'Metabolic changes are closely linked to PCOS and insulin resistance.',
    icon: '📊',
    questions: [
      {
        id: 'weightGain',
        label: 'Have you experienced unexplained weight gain, especially around the abdomen?',
        type: 'toggle',
        options: [
          { label: 'No',                   score: 0 },
          { label: 'Mild (1–5 kg)',         score: 1 },
          { label: 'Moderate (5–10 kg)',    score: 2 },
          { label: 'Significant (10+ kg)', score: 3 },
        ],
      },
      {
        id: 'sugarCraving',
        label: 'Do you have frequent sugar cravings or energy crashes?',
        type: 'toggle',
        options: [
          { label: 'Never',          score: 0 },
          { label: 'Sometimes',      score: 1 },
          { label: 'Often',          score: 2 },
          { label: 'Almost always',  score: 3 },
        ],
      },
      {
        id: 'diabetesHistory',
        label: 'Do you have a family history of type 2 diabetes?',
        type: 'toggle',
        options: [
          { label: 'No',                   score: 0 },
          { label: 'Distant relatives',    score: 1 },
          { label: 'Parent or sibling',    score: 2 },
        ],
      },
    ],
  },

  {
    id: 'fertility',
    stepLabel: 'Fertility Context',
    title: 'Fertility & Reproductive Health',
    subtitle: 'PCOS is one of the most common causes of fertility challenges.',
    icon: '🌸',
    questions: [
      {
        id: 'tryingToConceive',
        label: 'Are you currently trying to conceive?',
        type: 'toggle',
        options: [
          { label: 'No',                   score: 0 },
          { label: 'Yes',                  score: 0 },
          { label: 'Planning to in future',score: 0 },
        ],
      },
      {
        id: 'difficultyConceiving',
        label: 'Have you experienced difficulty conceiving?',
        type: 'toggle',
        conditional: { field: 'tryingToConceive', notValue: 'No' },
        options: [
          { label: 'No difficulties',             score: 0 },
          { label: 'Trying < 12 months',          score: 1 },
          { label: 'Trying 12–24 months',         score: 2 },
          { label: 'Trying > 24 months',          score: 3 },
        ],
      },
      {
        id: 'ovulationTracking',
        label: 'Do you track ovulation? If so, how are the results?',
        type: 'toggle',
        options: [
          { label: 'Not tracking',          score: 0 },
          { label: 'Tracking — regular',    score: 0 },
          { label: 'Tracking — irregular',  score: 2 },
          { label: 'Tracking — no ovulation', score: 3 },
        ],
      },
    ],
  },

  {
    id: 'emotional',
    stepLabel: 'Emotional Wellness',
    title: 'Emotional Wellness',
    subtitle: 'Hormonal imbalances from PCOS can significantly impact emotional health.',
    icon: '💜',
    questions: [
      {
        id: 'stressLevel',
        label: 'How would you rate your current stress levels?',
        type: 'toggle',
        options: [
          { label: 'Low',       score: 0 },
          { label: 'Moderate',  score: 1 },
          { label: 'High',      score: 2 },
          { label: 'Very high', score: 3 },
        ],
      },
      {
        id: 'anxietyLevel',
        label: 'Do you experience anxiety or low mood?',
        type: 'toggle',
        options: [
          { label: 'Rarely / Never', score: 0 },
          { label: 'Sometimes',      score: 1 },
          { label: 'Often',          score: 2 },
          { label: 'Most of the time',score:3 },
        ],
      },
      {
        id: 'overwhelmed',
        label: 'Do you feel overwhelmed managing your symptoms?',
        type: 'toggle',
        options: [
          { label: 'No',        score: 0 },
          { label: 'Sometimes', score: 1 },
          { label: 'Often',     score: 2 },
        ],
      },
    ],
  },
];

// ── Review sections ────────────────────────────────────────────────────────────
export const PCOS_REVIEW_SECTIONS = [
  { id: 'menstrual', icon: '🗓️', label: 'Menstrual Pattern',    fields: ['cycleRegularity', 'cycleLength', 'periodsPerYear'] },
  { id: 'hormonal',  icon: '⚗️', label: 'Hormonal Signs',       fields: ['acne', 'hairGrowth', 'hairThinning'] },
  { id: 'metabolic', icon: '📊', label: 'Metabolic Indicators', fields: ['weightGain', 'sugarCraving', 'diabetesHistory'] },
  { id: 'fertility', icon: '🌸', label: 'Fertility Context',     fields: ['tryingToConceive', 'difficultyConceiving', 'ovulationTracking'] },
  { id: 'emotional', icon: '💜', label: 'Emotional Wellness',   fields: ['stressLevel', 'anxietyLevel', 'overwhelmed'] },
];

// ── Scoring ────────────────────────────────────────────────────────────────────
const SCREEN_WEIGHTS = { menstrual:1.4, hormonal:1.2, metabolic:1.0, fertility:0.8, emotional:0.6 };
const SCREEN_MAX     = { menstrual:8, hormonal:9, metabolic:8, fertility:6, emotional:8 };

export function computePCOSScore(answers) {
  let weighted = 0, maxWeighted = 0;
  PCOS_QUESTION_SCREENS.forEach((screen) => {
    const w = SCREEN_WEIGHTS[screen.id];
    const maxRaw = SCREEN_MAX[screen.id];
    let raw = 0;
    screen.questions.forEach((q) => {
      const val = answers[q.id];
      if (!val) return;
      const opt = q.options.find((o) => o.label === val);
      if (opt) raw += opt.score ?? 0;
    });
    weighted    += raw * w;
    maxWeighted += maxRaw * w;
  });
  return maxWeighted > 0 ? Math.round((weighted / maxWeighted) * 100) : 0;
}

// ── Risk levels ────────────────────────────────────────────────────────────────
export function getPCOSRisk(pct) {
  if (pct < 30) return {
    level:'Low Risk', emoji:'✅', color:'#16a34a', bg:'#f0fdf4', border:'#bbf7d0',
    desc:'Your responses suggest a lower likelihood of PCOS. Continue maintaining a healthy lifestyle.',
    actions: [
      { icon:'📅', title:'Schedule a routine wellness check', desc:'Annual checkup to monitor your hormonal health.' },
      { icon:'🥗', title:'Maintain healthy diet & exercise',  desc:'Lifestyle choices significantly affect hormonal balance.' },
      { icon:'📊', title:'Track your symptoms regularly',     desc:'Use our symptom tracker to monitor any changes.' },
    ],
    means: "Your answers suggest relatively few PCOS indicators. Continue regular health monitoring and self-care.",
  };
  if (pct < 60) return {
    level:'Moderate Risk', emoji:'⚠️', color:'#d97706', bg:'#fffbeb', border:'#fde68a',
    desc:'Some indicators suggest a moderate risk of PCOS. A clinical evaluation is recommended.',
    actions: [
      { icon:'👩‍⚕️', title:'Book a Gynaecologist Consultation', desc:'A specialist can evaluate your symptoms in depth.' },
      { icon:'🔬', title:'Start Full PCOS Assessment',          desc:'One deeper, in-depth assessment and personalized insights.' },
      { icon:'📊', title:'Lifestyle & Symptom Tracker',         desc:'Track your cycles, symptoms and wellness journey.' },
    ],
    means: "You have several signs associated with PCOS. A professional evaluation can help with early diagnosis and better management.",
  };
  return {
    level:'High Risk', emoji:'🔴', color:'#dc2626', bg:'#fef2f2', border:'#fecaca',
    desc:'Multiple PCOS indicators present. Please consult a gynaecologist or endocrinologist promptly.',
    actions: [
      { icon:'🚨', title:'Book Urgent Consultation',     desc:'Multiple indicators detected — see a specialist soon.' },
      { icon:'🔬', title:'Full PCOS Assessment',         desc:'Comprehensive testing and personalized care plan.' },
      { icon:'💊', title:'Join PCOS Care Program',       desc:'Expert-led programme for lifestyle, nutrition and hormones.' },
    ],
    means: "Multiple significant PCOS indicators are present. Early diagnosis and treatment can greatly improve your quality of life.",
  };
}
